import { IS_TEST } from '~/utils/constants';
import { parseAddress, PlaceAddressParts } from './parse-address';

export type { PlaceAddressParts };

export type GooglePlaceMatch = {
  parsed: PlaceAddressParts;
  formattedAddress: string;
};

export const streetNumberFromStreet = (street?: string): string | undefined => {
  const match = street?.trim().match(/^(\d+)/);
  return match?.[1];
};

const isCompletePlaceAddress = (parsed: PlaceAddressParts): boolean =>
  Boolean(parsed.street && parsed.city && parsed.state && parsed.zipCode);

export const isConfidentGooglePlaceMatch = (
  extracted: PlaceAddressParts,
  predictionText: string,
  parsed: PlaceAddressParts,
): boolean => {
  if (!isCompletePlaceAddress(parsed)) return false;

  const streetNumber = streetNumberFromStreet(extracted.street);
  if (!streetNumber) return false;

  const parsedStreetNumber = streetNumberFromStreet(parsed.street);
  const escapedStreetNumber = streetNumber.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const streetNumberPattern = new RegExp(`\\b${escapedStreetNumber}\\b`);
  return parsedStreetNumber === streetNumber || streetNumberPattern.test(predictionText);
};

const getPlacePredictions = (
  service: google.maps.places.AutocompleteService,
  query: string,
): Promise<google.maps.places.AutocompletePrediction[] | null> =>
  new Promise(resolve => {
    service.getPlacePredictions(
      {
        input: query,
        componentRestrictions: { country: 'us' },
        types: ['address'],
      },
      (predictions, status) => {
        const ok =
          status === google.maps.places.PlacesServiceStatus.OK ||
          status === ('OK' as typeof status);
        if (!ok || !predictions?.length) {
          resolve(null);
          return;
        }
        resolve(predictions);
      },
    );
  });

const getPlaceDetails = (
  service: google.maps.places.PlacesService,
  placeId: string,
): Promise<google.maps.places.PlaceResult | null> =>
  new Promise(resolve => {
    service.getDetails(
      {
        placeId,
        fields: ['address_components', 'formatted_address'],
      },
      (place, status) => {
        const ok =
          status === google.maps.places.PlacesServiceStatus.OK ||
          status === ('OK' as typeof status);
        if (!ok || !place) {
          resolve(null);
          return;
        }
        resolve(place);
      },
    );
  });

export const fetchGooglePlaceMatch = async (
  query: string,
  extracted: PlaceAddressParts,
): Promise<GooglePlaceMatch | null> => {
  const trimmed = query.trim();
  if (!trimmed) return null;

  try {
    await google.maps.importLibrary('places');
    const autocompleteService = new google.maps.places.AutocompleteService();
    const placesService = new google.maps.places.PlacesService(document.createElement('div'));

    const predictions = await getPlacePredictions(autocompleteService, trimmed);
    if (!predictions?.length) return null;

    for (const prediction of predictions) {
      if (prediction.place_id) {
        const place = await getPlaceDetails(placesService, prediction.place_id);
        if (place?.address_components) {
          const parsed = parseAddress(
            place.address_components as Parameters<typeof parseAddress>[0],
          );
          const formattedAddress = place.formatted_address || prediction.description || '';
          const predictionText = `${prediction.description || ''} ${formattedAddress}`;

          if (isConfidentGooglePlaceMatch(extracted, predictionText, parsed)) {
            return { parsed, formattedAddress };
          }
        }
      }
    }

    return null;
  } catch {
    return null;
  }
};

export const matchGooglePlace = (
  query: string,
  extracted: PlaceAddressParts,
): Promise<GooglePlaceMatch | null> => {
  if (IS_TEST) {
    return Promise.resolve(
      isConfidentGooglePlaceMatch(extracted, query, extracted)
        ? { parsed: extracted, formattedAddress: query }
        : null,
    );
  }

  return fetchGooglePlaceMatch(query, extracted);
};
